import React, { useState, useEffect } from 'react';
import {
  Layout, Row, Col, Typography, Card, Form, Modal, Spin, message,
} from 'antd';
import styled from 'styled-components';
import { useTranslation, I18nextProvider, Trans } from 'react-i18next';
import { ReactComponent as SiderCo3Logo } from '../../assets/co3-logo-dark-100.svg';
import {
  CreateAccountOrLogin, EuInvitation, FillCompanyData, InviteSteps, UsInvitation,
} from '../../components/steps';
import { FlowType, RegionCode } from '../../types';
import { CompanyService, UserService } from '../../services';
import { fromQueryParam, RedirectType } from './redirectType.enum';
import translations from '../../config/translations';
import { FormConfig } from '../../config/formConfig';
import { isValidUUID } from '../../utils/uuid';

const InviteLayout = styled(Layout)`
  background: #fdfefe;
`;

const InviteContent = styled(Layout.Content)`
  background: #e5e5e5;
  border-top-left-radius: 25px;
`;

const InviteSider = styled(Layout.Sider)`
  background: #fdfefe;
`;

const SetUpText = styled(Typography.Text)`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  display: flex;
  align-items: center;
  text-align: center;
  text-transform: uppercase;

  color: #489ed7;
`;

const CreateAccountCard = styled(Card)`
  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  box-sizing: border-box;
  box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  width: 634px;
`;

const RedirectTypeStep = new Map([
  [RedirectType.GOOGLE, 0],
  [RedirectType.NONE, -1],
]);

const Onboarding = () => {
  const { t } = useTranslation();
  const { search } = window.location;
  const params = new URLSearchParams(search);
  const companyId = params.get('companyId');
  const userId = params.get('userId');
  const redirectType = fromQueryParam(params.get('redirect'));
  if (redirectType === RedirectType.LOGOUT) {
    UserService.loginByGoogle();
  }
  const [step, setStep] = useState(undefined);
  const [createAccountForm] = Form.useForm();
  const [uuid, setUuid] = useState('');
  const [company, setCompany] = useState(undefined);
  const [regionFormConfig, setRegionFormConfig] = useState(undefined);
  const [spin, setSpin] = useState(true);

  const invalidInvitationLinkModal = () => {
    setStep(undefined);
    Modal.error({
      title: t('INVALID_INTITATION_LINK_MODAL_HEADER'),
      content: (
        <>
          <I18nextProvider i18n={translations.invite}>
            <Trans
              i18nKey="INVALID_INTITATION_LINK_MODAL_CONTENT"
            >
              <a href={`mailto:${t('COMMON.SUPPORT')}`}>{t('COMMON.SUPPORT')}</a>
            </Trans>
          </I18nextProvider>
        </>),
    });
  };

  const handleError = (e) => {
    // eslint-disable-next-line
    console.error(e);
    const errorCode = e.response?.data?.errorCode;
    if (errorCode === 'INVALID_INVITATION_LINK' || errorCode === 'INVALID_DATA') {
      invalidInvitationLinkModal();
    } else {
      message.error(t('GENERIC_ERROR'));
    }
  };

  useEffect(() => {
    if (!companyId || (userId !== null && !isValidUUID(userId))) {
      invalidInvitationLinkModal();
      setSpin(false);
      return;
    }
    CompanyService.getCompany(companyId)
      .then((payload) => {
        if (payload?.name && payload?.region) {
          setCompany(payload);
          setRegionFormConfig(FormConfig[payload.region]);
          setStep(RedirectTypeStep.get(redirectType));
        } else {
          invalidInvitationLinkModal();
        }
      })
      .catch((e) => {
        handleError(e);
      })
      .finally(() => setSpin(false));
  }, []);

  const setNextStep = () => {
    setStep(step + 1);
  };

  const stepView = (s) => {
    switch (s) {
      case -1: {
        return (
          <CreateAccountCard>
            {company.region === RegionCode.US ? (
              <UsInvitation
                companyName={company.name}
                setStep={() => setNextStep()}
                regionFormConfig={regionFormConfig}
              />
            ) : (
              <EuInvitation
                companyName={company.name}
                setStep={() => setNextStep()}
                regionFormConfig={regionFormConfig}
              />
            )}
          </CreateAccountCard>
        );
      }
      case 0:
        return (
          <>
            <CreateAccountCard>
              <CreateAccountOrLogin
                form={createAccountForm}
                companyId={companyId}
                companyName={company.name}
                regionFormConfig={regionFormConfig}
                userId={userId}
                setStep={() => setNextStep()}
                setUuid={(e) => setUuid(e)}
                redirectType={redirectType}
              />
            </CreateAccountCard>
          </>
        );
      case 1:
        return (
          <>
            <FillCompanyData
              uuid={uuid}
              regionFormConfig={regionFormConfig}
              password={createAccountForm.getFieldValue('password')}
              username={createAccountForm.getFieldValue('email')}
              flowType={FlowType.ONBOARDING}
            />
          </>
        );
      default:
        return (<></>);
    }
  };

  return (
    <Spin spinning={spin} style={{ maxHeight: '100%' }}>
      <InviteLayout>
        <InviteSider width="30%" style={{ height: '100vh' }}>
          <Row justify="space-between" style={{ width: '100%', marginBottom: '60px' }}>
            <Col span={24} />
          </Row>
          <Row style={{ width: '100%', margin: '0 auto', marginBottom: '60px' }} justify="center">
            <Col>
              <SiderCo3Logo />
            </Col>
          </Row>
          <Row justify="center" style={{ width: '100%', marginBottom: '14px' }}>
            <Col className="gutter-row">
              <SetUpText>{t('SET_UP_CARRIER_ACCOUNT_TEXT')}</SetUpText>
            </Col>
          </Row>
          <Row justify="center">
            <Col>
              <InviteSteps currentStep={step} />
            </Col>
          </Row>
        </InviteSider>
        <InviteContent height="100vh">
          <Row justify="space-between" style={{ width: '100%', marginBottom: '100px' }}>
            <Col span={24} />
          </Row>
          <Row justify="center" style={{ marginBottom: '18px' }}>
            {stepView(step)}
          </Row>
        </InviteContent>
      </InviteLayout>
    </Spin>
  );
};

export default Onboarding;
